import clsx from 'clsx';
import React, { useState } from 'react';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import Select from '../../components/Select';
import moment from 'moment';
import Button from '../../components/Button';
import ReportFileFilledIcon from '../../assets/icons/report-file-filled-icon.svg?react';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import { toggleModal } from '../../reducers/modalToggle';
import ModalComponent from '../../components/ModalComponent';
import CreateEstablishmentModal from '../../components/CreateEstablishmentModal';
import { useFormik } from 'formik';
import { getCreateEstablishmentSchema } from './schema';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';

import { toast } from 'react-toastify';
import { useApiClients } from '../../client/api/useApiClient';
import {
  CreateEstablishmentDtoEstablishmentTypeEnum,
  CreateEstablishmentDtoSizeEnum,
  GetEstablishmentResponseDto,
  GetYearStatusForOshaSubmissionResponse,
} from '@EHSNavigator/backend';
import { useNavigate } from 'react-router';
import { queryClient } from '../../main';
import { useQuery } from '@tanstack/react-query';
import Loader from '../../components/Loader';
import { dateOshaValidation } from '../../utils/oshaDateValidation';
import { STATES } from '../../components/CreateEstablishmentModal/contants';

interface OshaReportingProps {
  otherClasses?: string;
  data: GetEstablishmentResponseDto['establishment'];
}

const ESTABLISHMENT_TYPE = [
  {
    label: 'Non Government',
    value: '1',
  },
  {
    label: 'State Government',
    value: '2',
  },
  {
    label: 'Local Government',
    value: '3',
  },
];

const ESTABLISHMENT_SIZE = [
  {
    label: 'less than 20 employees',
    value: '1',
  },
  {
    label: '20–99 employees',
    value: '21',
  },
  {
    label: '100–249 employees',
    value: '22',
  },
  {
    label: '250+ employees',
    value: '3',
  },
];

const OshaReporting: React.FC<OshaReportingProps> = ({
  otherClasses,
  data,
}) => {
  const oshaReportingClasses = clsx(otherClasses);
  const { OshaReportingApi, EstablishmentApi } = useApiClients();
  const navigate = useNavigate();
  const [state, setState] = useState({
    year: '2024',
  });
  const dispatch = useDispatch();
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );

  const sm = useButtonSizeSmall();

  // const currentYear = moment().year();
  // const list: { label: string; value: string }[] = [];

  // list.push({
  //   label: (currentYear - 1).toString(),
  //   value: (currentYear - 1).toString(),
  // });

  const startYear = 2025;
  const currentYear = moment().year();

  const list: { label: string; value: string }[] = [];

  for (let year = startYear; year <= currentYear; year++) {
    list.push({
      label: year.toString(),
      value: year.toString(),
    });
  }
  const createEstablishmentHandler = useFormik({
    initialValues: {
      establishmentName: data?.establishment_name ?? '',
      street: data?.address?.street ?? '',
      city: data?.address?.city ?? '',
      state: data?.address?.state ?? '',
      zip: data?.address?.zip ?? '',
      industryDescription: data?.industry_description ?? '',
      classification: data?.naics ?? '',
      establishment_type: data?.establishment_type?.toString() ?? '',
      size: data?.size?.toString() ?? '',
    },

    enableReinitialize: true,
    validationSchema: getCreateEstablishmentSchema(),
    onSubmit: async (values) => {
      try {
        let res;
        if (data === null) {
          res =
            await EstablishmentApi.establishmentControllerCreateEstablishment({
              createEstablishmentDto: {
                establishment_name: values?.establishmentName || '',
                city: values?.city || '',
                naics: values?.classification?.toString() || '',
                state: values?.state || '',
                street: values?.street || '',
                zip: values?.zip?.toString() || '',
                industryDescription: values?.industryDescription || '',
                establishment_type:
                  (Number(
                    values?.establishment_type,
                  ) as CreateEstablishmentDtoEstablishmentTypeEnum) || '',
                size:
                  (Number(values?.size) as CreateEstablishmentDtoSizeEnum) ||
                  '',
              },
            });
        } else {
          res =
            await EstablishmentApi.establishmentControllerUpdateEstablishment({
              updateEstablishmentDto: {
                establishment_name: values?.establishmentName || '',
                city: values?.city || '',
                naics: values?.classification?.toString() || '',
                state: values?.state || '',
                street: values?.street || '',
                zip: values?.zip?.toString() || '',
                industryDescription: values?.industryDescription || '',
                establishment_type:
                  (Number(
                    values?.establishment_type,
                  ) as CreateEstablishmentDtoEstablishmentTypeEnum) || '',
                size:
                  (Number(values?.size) as CreateEstablishmentDtoSizeEnum) ||
                  '',
              },
            });
        }

        queryClient.setQueryData(
          ['get-establishment'],
          (oldData: GetEstablishmentResponseDto['establishment']) => {
            return { ...oldData, ...res.data.establishment };
          },
        );

        toast.success(res?.data?.message);
        createEstablishmentHandler.resetForm();
        dispatch(toggleModal(''));
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error || error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const {
    data: oshaGenerateStatus,
    isLoading,
    isRefetching,
  } = useQuery<GetYearStatusForOshaSubmissionResponse['hasSubmitted'], Error>({
    queryKey: ['get-osha-reporting-status', state?.year],
    queryFn: async () => {
      const response =
        await OshaReportingApi.oshaReportingControllerGetYearStatusForOshaSubmission(
          {
            year: Number(state?.year),
          },
        );
      return response?.data?.hasSubmitted;
    },
    enabled: !!state?.year, // 👈 only run when year is available
  });

  const isDateAvailable = dateOshaValidation();

  if (isLoading || isRefetching) {
    return <Loader />;
  }

  return (
    <div className={oshaReportingClasses} data-testid="osha-reporting">
      <div className="flex items-center gap-2.5 lg:gap-10 xl:gap-20">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div className="flex-1">
          <h1 className="h3">OSHA Reporting</h1>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
            Send all injuries and illness records to OSHA for the year{' '}
            <Select
              filterSelect
              list={[{ label: '2024', value: '2024' }, ...list]}
              value={state?.year}
              placeholder="Status"
              otherClasses={{
                container: 'w-fit inline-block',
                button:
                  ' !bg-transparent !ring-0  !p-0 !text-brand-byzantine !text-xl !font-bold [&>svg>path]:!fill-current',
                listContainer: '!top-8  !p-2 ',
                list: '!py-1 [&>button]:text-center',
              }}
              onSelect={(e) =>
                setState((prev) => ({
                  ...prev,
                  year: e,
                }))
              }
              errorMessage=""
            />
          </p>
          <p className="font-inter-variable text-brand-posh-pink -tracking-button mt-2 hidden text-xl leading-6 font-medium lg:block">
            <WarningIcon className="[&>path]:fill-brand-posh-pink inline-block h-7 w-7 -translate-y-0.5" />{' '}
            OSHA reporting is only open from Jan 2 to Mar 2. Reporting is
            currently unavailable outside this period.
          </p>
        </div>
        {data === null && (
          <Button
            size="small"
            otherClasses="hidden lg:flex"
            variant="primary"
            onClick={() => dispatch(toggleModal('create-establishment'))}
          >
            Create Establishment
          </Button>
        )}
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 mb-15 text-lg leading-5 font-medium lg:hidden">
        Send all injuries and illness records to OSHA for the year{' '}
        <Select
          filterSelect
          list={[{ label: '2024', value: '2024' }, ...list]}
          value={state?.year}
          placeholder="Status"
          otherClasses={{
            container: 'w-fit inline-block',
            button:
              ' !bg-transparent !ring-0  !p-0 !text-brand-byzantine !text-lg !font-bold [&>svg>path]:!fill-current',
            listContainer: '!top-8  !p-2 ',
            list: '!py-1 [&>button]:text-center',
          }}
          onSelect={(e) =>
            setState((prev) => ({
              ...prev,
              year: e,
            }))
          }
          errorMessage=""
        />
      </p>
      {data === null && (
        <Button
          size="smaller"
          otherClasses="flex lg:hidden w-full sm:w-fit mb-4"
          variant="primary"
          onClick={() => dispatch(toggleModal('create-establishment'))}
        >
          Create Establishment
        </Button>
      )}
      <p className="font-inter-variable text-brand-posh-pinkbrand-posh-pink -tracking-text-area mb-15 text-lg leading-5 font-medium lg:hidden">
        <WarningIcon className="[&>path]:fill-brand-posh-pink inline-block h-5 w-5 -translate-y-0.5" />{' '}
        OSHA reporting is only open from Jan 2 to Mar 2. Reporting is currently
        unavailable outside this period.
      </p>

      <div className="mt-8 flex max-w-766 flex-col items-start lg:mt-24">
        {oshaGenerateStatus ? (
          <Button
            variant="primary"
            size={sm}
            href={`/osha-reporting/logs?year=${state?.year}`}
            otherClasses="bg-brand-fair-orange hover:border-brand-fair-orange hover:text-brand-fair-orange"
          >
            <ReportFileFilledIcon className="h-6 w-6 [&>path]:fill-current" />{' '}
            View Log's
          </Button>
        ) : (
          <Button
            variant="primary"
            size={sm}
            disable={!isDateAvailable || data === null}
            onClick={() =>
              navigate('/osha-reporting/report', {
                state: { year: state?.year },
              })
            }
          >
            <ReportFileFilledIcon className="h-6 w-6 [&>path]:fill-current" />{' '}
            Generate Form 300
          </Button>
        )}
        {!oshaGenerateStatus && (
          <p className="font-inter-variable text-neutral-tones-text mt-8 text-base leading-5 font-medium italic lg:mt-25 lg:text-xl lg:leading-6">
            This will compile all your data into the OSHA 300 form. You will be
            able to review the data before submission.
          </p>
        )}
      </div>
      {data !== null && (
        <div className="border-t-neutral-tones-text/35 mt-9 w-full border-t pt-8">
          <div className="wfull flex items-center justify-between gap-15">
            <h4 className="text-brand-byzantine">Establishment Details</h4>
            <Button
              onClick={() => dispatch(toggleModal('create-establishment'))}
              size="smaller"
              otherClasses="w-121"
            >
              <EditIcon className="h-5 w-5 [&>path]:fill-current" /> Edit
            </Button>
          </div>
          <div className="mt-6 grid grid-cols-2 justify-between gap-x-9 gap-y-6 sm:grid-cols-3 xl:grid-cols-5">
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Name
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {data?.establishment_name || '---'}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Street
              </h6>
              <p className="font-inter-variable text-neutral-tones-text truncate text-base leading-5 font-normal">
                {data?.address?.street || '---'}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                State
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {STATES?.find(({ value }) => value === data?.address?.state)
                  ?.label || '---'}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                City
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {data?.address?.city || '---'}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Establishment Type
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {ESTABLISHMENT_TYPE?.find(
                  (node) => node?.value === String(data?.establishment_type),
                )?.label || '---'}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Size
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {ESTABLISHMENT_SIZE?.find(
                  (node) => node?.value === String(data.size),
                )?.label || '---'}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Zip
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {data?.address?.zip || '---'}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Industry description
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {data?.industry_description || '---'}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                NAICS
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {data?.naics || '---'}
              </p>
            </div>
          </div>
        </div>
      )}
      {['create-establishment'].includes(modalName) && (
        <ModalComponent
          otherClasses="px-4 lg:px-0 py-6 "
          additionalClass={clsx(
            'p-5 lg:p-6 rounded-2xl h-fit max-h-full max-w-3xl w-full',
          )}
          customClose={() => {
            dispatch(toggleModal(''));
          }}
        >
          {modalName === 'create-establishment' && (
            <CreateEstablishmentModal
              handleClose={() => {
                createEstablishmentHandler?.resetForm();
                dispatch(toggleModal(''));
              }}
              formHandler={createEstablishmentHandler}
              editEstablishment={data !== null}
            />
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default OshaReporting;
